import { NumberDecimalFormat } from '@components/shared/Format'
import { Constants } from '@models/Constants'
import { CountryData } from '@models/Country'
import { ComputedFrontline } from '@models/Frontline'
import { getBonusPercentLevel } from '@models/PerformanceBonus'
import { VersionStorage } from '@models/Version'
import { BronzeConsistencyIncentiveStore } from '@stores/BronzeConsistencyIncentive.store'
import { BronzeIncentivesStore } from '@stores/BronzeIncentives.store'
import { FrontlineStore } from '@stores/Frontline.store'
import { GlobalStore } from '@stores/Global.store'
import { LeadershipPlusIncentiveStore } from '@stores/LeadershipPlusIncentive.store'
import { PathToBronzeStore } from '@stores/PathToBronze.store'
import { PerformancePlusStore } from '@stores/PerformancePlus.store'
import { TwoTimeCashIncentiveStore } from '@stores/TwoTimeCashIncentive.store'
import { BronzeConsistencyIncentiveCalculation } from '@utils/calculations/BronzeConsistencyIncentive.calculation'
import { BronzeIncentiveCalculation } from '@utils/calculations/BronzeIncentive.calculation'
import { DepthCalculation } from '@utils/calculations/Depth.calculation'
import { LeadershipPlusIncentiveCalculation } from '@utils/calculations/LeadershipPlusIncentive.calculation'
import { PathToBronzeCalculation } from '@utils/calculations/PathToBronze.calculation'
import { PerformancePlusCalculation } from '@utils/calculations/PerformancePlus.calculation'
import { SummaryCalculation } from '@utils/calculations/Summary.calculation'
import { TwoTimeCashIncentiveCalculation } from '@utils/calculations/TwoTimeCashIncentive.calculation'
import { dataCountries, dataVersions } from '@utils/data'
import { CountryHelper } from '@utils/helpers/CountryHelper'
import { LocalizationHelper } from '@utils/helpers/LocalizationHelper'
import { VersionHelper } from '@utils/helpers/VersionHelper'
import { makeAutoObservable } from 'mobx'
import { clearPersistedStore, configurePersistable } from 'mobx-persist-store'

export interface StoreProps {
  store: Store
}

configurePersistable({ storage: window.localStorage, stringify: true, debugMode: false })

export class Store {
  // Stores
  globalStore = new GlobalStore()
  frontlineStore = new FrontlineStore()
  bronzeIncentivesStore = new BronzeIncentivesStore()
  bronzeConsistencyIncentiveStore = new BronzeConsistencyIncentiveStore()
  pathToBronzeStore = new PathToBronzeStore()
  performancePlusStore = new PerformancePlusStore()
  leadershipPlusIncentiveStore = new LeadershipPlusIncentiveStore()
  twoTimeCashIncentiveStore = new TwoTimeCashIncentiveStore()

  // Helper
  countryHelper = new CountryHelper(this)
  versionHelper = new VersionHelper(this)
  localizationHelper = new LocalizationHelper(this)

  // calculations
  depthCalculation = new DepthCalculation(this)
  bronzeIncentiveCalculation = new BronzeIncentiveCalculation(this)
  bronzeConsistencyIncentiveCalculation = new BronzeConsistencyIncentiveCalculation(this)
  pathToBronzeCalculation = new PathToBronzeCalculation(this)
  performancePlusCalculation = new PerformancePlusCalculation(this)
  leadershipPlusIncentiveCalculation = new LeadershipPlusIncentiveCalculation(this)
  twoTimeCashIncentiveCalculation = new TwoTimeCashIncentiveCalculation(this)
  summaryCalculation = new SummaryCalculation(this)

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  // Global

  get selectedUA(): boolean {
    return this.countryHelper.selectedUA
  }

  get selectedANZ(): boolean {
    return this.countryHelper.selectedANZ
  }

  get selectedUKANZ(): boolean {
    return this.countryHelper.selectedUKANZ
  }

  get selectedLanguageZH(): boolean {
    return this.countryHelper.selectedLanguageZH
  }

  get performanceBonus(): number {
    return this.personalPerformanceBonus + this.differentialBonus
  }

  get performanceBonusAnnual(): number {
    return this.performanceBonus * 12
  }

  get personalPerformanceBonus(): number {
    return NumberDecimalFormat(this.personalBV * this.performanceLevelPercent)
  }

  get differentialBonus(): number {
    return this.computedFrontlines.map((frontline) => frontline.performanceDifferential).reduce((sum, performanceDifferential) => sum + performanceDifferential, 0)
  }

  get personalPV() {
    const { personalConsumptionPV, verifiedCustomerSalesPV } = this.globalStore
    return personalConsumptionPV + verifiedCustomerSalesPV
  }

  get personalAnnualPV() {
    return this.personalPV * 12
  }

  get personalBV() {
    return this.personalConsumptionBV + this.verifiedCustomerSalesBV
  }

  get groupPV() {
    const frontlinesGroupPv = this.computedFrontlines.map((frontline) => {
      if (frontline.bonusPercentLevel < Constants.BONUS_LEVEL_MAX_PERCENT) { return frontline.groupPV }
      return 0
    }).reduce((sum, groupPV) => sum + groupPV, 0)
    return frontlinesGroupPv + this.personalPV
  }

  get groupBV() {
    const groupBV = NumberDecimalFormat(this.groupPV * this.ratioPVBV)
    return groupBV
  }

  get rubyPV() {
    const frontlinesRubyPV = this.computedFrontlines.map((frontline) => {
      if (frontline.bonusPercentLevel < Constants.BONUS_LEVEL_MAX_PERCENT && !frontline.rubyVolume) { return frontline.groupPV }
      return 0
    }).reduce((sum, groupPV) => sum + groupPV, 0)
    return frontlinesRubyPV + this.personalPV
  }

  get rubyBV() {
    const frontlinesRubyBV = this.computedFrontlines.map((frontline) => {
      if (frontline.rubyVolume) { return 0 }
      return frontline.bonusPercentLevel < Constants.BONUS_LEVEL_MAX_PERCENT ? frontline.groupBV : 0
    }).reduce((sum, groupPV) => sum + groupPV, 0)
    const rubyBV = NumberDecimalFormat(frontlinesRubyBV + this.personalBV)
    return rubyBV
  }

  get rubyBonus(): number {
    return this.countryHelper.rubyBonus
  }

  get rubyBonusAnnual(): number {
    return this.rubyBonus * 12
  }

  get frontlinesLeadershipBonus(): ComputedFrontline[] {
    return this.computedFrontlines.filter((frontline) => frontline.leadershipBonus > 0)
  }

  get frontlinesLeadershipBonusAmount(): number {
    return NumberDecimalFormat(this.frontlinesLeadershipBonus.reduce((sum, frontline) => sum + frontline.leadershipBonus, 0))
  }

  get leadershipBonus(): number {
    if (this.frontlinesLeadershipBonus.length > 0) {
      return this.leadershipBonusMax
    }

    return 0
  }

  get leadershipBonusAnnual(): number {
    return this.leadershipBonus * 12
  }

  get leadershipBonusMax(): number {
    return Math.max(this.leadershipBonusKeepAll, this.leadershipBonusKeepSome1Leg, this.leadershipBonusKeepSome2Leg)
  }

  get leadershipBonusLBA(): number {
    const qmonthGroupPVFrontlines = this.countryHelper.qmonthGroupPVFrontlines
    return qmonthGroupPVFrontlines * this.ratioPVBV * Constants.LEADERSHIP_BONUS_PERCENT
  }

  get leadershipBonusSixPercentBV(): number {
    return Constants.LEADERSHIP_BONUS_PERCENT * this.groupBV
  }

  get leadershipBonusAdjustment(): number {
    const adjustment = this.leadershipBonusSixPercentBV - this.leadershipBonusLBA
    if (adjustment < 0) {
      return NumberDecimalFormat(adjustment)
    }

    return 0
  }

  get leadershipBonusKeepAll(): number {
    const qmonthGroupPVFrontlines = this.countryHelper.qmonthGroupPVFrontlines
    if (this.frontlinesLeadershipBonus.length > 0 && this.groupPV >= qmonthGroupPVFrontlines) {
      return this.frontlinesLeadershipBonusAmount
    }

    return 0
  }

  get leadershipBonusKeepSome1Leg(): number {
    const qmonthGroupPVFrontlines = this.countryHelper.qmonthGroupPVFrontlines
    const qmonthGroupPVFrontline = this.countryHelper.qmonthGroupPVFrontline
    if (this.frontlinesLeadershipBonus.length === 1 && this.groupPV >= qmonthGroupPVFrontline && this.groupPV < qmonthGroupPVFrontlines) {
      return this.frontlinesLeadershipBonusAmount + this.leadershipBonusAdjustment
    }

    return 0
  }

  get leadershipBonusKeepSome2Leg(): number {
    if (this.frontlinesLeadershipBonus.length > 1) {
      return this.frontlinesLeadershipBonusAmount + this.leadershipBonusAdjustment
    }

    return 0
  }

  get leadershipLevelPercent(): number {
    const downlineBonusPercent = Math.max(...this.computedFrontlines.map((frontline) => {
      if (frontline.fosterLeg && Constants.BONUS_LEVEL_MAX_PERCENT) { return 0 }
      return frontline.bonusPercentLevel
    }))
    const groupBonusPercent = getBonusPercentLevel(this.performanceBonusSchedule, this.groupPV)
    return Math.max(downlineBonusPercent, groupBonusPercent)
  }

  get frontlinesFosterBonus(): ComputedFrontline[] {
    return this.computedFrontlines.filter((frontline) => frontline.fosterBonus > 0)
  }

  get frontlinesFosterBonusAmount(): number {
    return NumberDecimalFormat(this.frontlinesFosterBonus.reduce((sum, frontline) => sum + frontline.fosterBonus, 0))
  }

  get fosterBonus(): number {
    return this.fosterBonusMax
  }

  get fosterBonusAnnual(): number {
    return this.fosterBonus * 12
  }

  get fosterBonusMax(): number {
    return Math.max(this.fosterBonusKeepAll1Leg, this.fosterBonusKeepSome2Leg)
  }

  get fosterBonusFBA(): number {
    const qmonthGroupPVFrontlines = this.countryHelper.qmonthGroupPVFrontlines
    return qmonthGroupPVFrontlines * this.ratioPVBV * Constants.FOSTER_BONUS_PERCENT
  }

  get fosterBonusKeepAll1Leg(): number {
    if (this.leadershipLevelPercent === Constants.BONUS_LEVEL_MAX_PERCENT) {
      return this.frontlinesFosterBonusAmount
    }

    return 0
  }

  get fosterBonusKeepSome2Leg(): number {
    if (this.leadershipLevelPercent < Constants.BONUS_LEVEL_MAX_PERCENT && this.frontlinesFosterBonus.length > 1) {
      return this.frontlinesFosterBonusAmount - this.fosterBonusFBA
    }

    return 0
  }


  // Business

  get qMonth(): boolean {
    const qmonthGroupPVFrontlines = this.countryHelper.qmonthGroupPVFrontlines
    const qmonthGroupPVFrontline = this.countryHelper.qmonthGroupPVFrontline
    const frontlinesGroupPv = this.computedFrontlines.filter((frontline) => frontline.bonusPercentLevel === Constants.BONUS_LEVEL_MAX_PERCENT)
    return ((this.groupPV >= qmonthGroupPVFrontlines) || frontlinesGroupPv.length > 1) || ((frontlinesGroupPv.length === 1) && (this.groupPV >= qmonthGroupPVFrontline))
  }


  // Performance

  get performanceLevelPercent(): number {
    const downlineBonusPercent = Math.max(...this.computedFrontlines.map(({ bonusPercentLevel }) => bonusPercentLevel))
    const groupBonusPercent = getBonusPercentLevel(this.performanceBonusSchedule, this.groupPV)
    return Math.max(downlineBonusPercent, groupBonusPercent)
  }

  get retailMargin(): number {
    return this.countryHelper.retailMarginPercent
  }

  get retailMarginAmount(): number {
    return NumberDecimalFormat(this.verifiedCustomerSalesBV * this.retailMargin)
  }

  get retailMarginAnnualAmount(): number {
    return this.retailMarginAmount * 12
  }


  // Getters

  get countryData(): CountryData {
    const version = dataVersions.find((dataVersion) => dataVersion.year === this.globalStore.versionYear)
    const countries = version?.countries ?? dataCountries
    return countries.find(({ country }) => country === this.globalStore.country)!
  }

  get ratioPVBV(): number { return this.countryData.ratioPVBV }

  get personalConsumptionBV(): number {
    const { personalConsumptionPV } = this.globalStore
    return personalConsumptionPV * this.ratioPVBV
  }

  get verifiedCustomerSalesBV(): number {
    const { verifiedCustomerSalesPV } = this.globalStore
    return verifiedCustomerSalesPV * this.ratioPVBV
  }

  get computedFrontlines(): ComputedFrontline[] {
    return this.frontlineStore.frontlines.map<ComputedFrontline>((input) => new ComputedFrontline(input, this))
  }

  get performanceBonusSchedule(): [number, number][] {
    return this.countryHelper.performanceBonusSchedule
  }

  // Actions

  async invalidatePersistedStore() {
    const { versionStorage } = this.globalStore
    if (versionStorage === VersionStorage) { return }

    // Stores deleted with the 2024 calculation are no longer in clearPersistedStore,
    // sweep their orphaned localStorage entries in the same one-time invalidation
    const legacyStoreNames = ['PersonalGroupGrowth', 'FrontlineGrowthIncentive', 'PerformanceIncentive']
    legacyStoreNames.forEach((name) => window.localStorage.removeItem(`${AIS_STORE}-${name}`))

    await this.clearPersistedStore()
    this.globalStore.setVersionStorage(VersionStorage)
  }

  async clearPersistedStore() {
    const stores = [
      this.globalStore, this.frontlineStore,
      this.bronzeIncentivesStore, this.bronzeConsistencyIncentiveStore,
      this.pathToBronzeStore, this.twoTimeCashIncentiveStore,
      this.performancePlusStore, this.leadershipPlusIncentiveStore
    ]
    await Promise.all(stores.map(async (store) => {
      await clearPersistedStore(store)
      store.reset()
    }))
  }
}

export const store = new Store()
